import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { PDFDownloadLink } from '@react-pdf/renderer';
import MyStrategy from './5-PDF';
import PWDModal from '../components/Modal';

const Summary = ({ topics, texts }) => {
    const { topicId } = useParams();

    const [topic, setTopic] = useState([]);
    const [answeredYesStatementIds, setAnsweredYesStatementIds] = useState([]);
    const [tipSelections, setTipSelections] = useState({});
    const [userTips, setUserTips] = useState({});
    const [newTipText, setNewTipText] = useState({});

    useEffect(() => {
        // Set topic
        const topic = topics.find(t => t.ID === Number(topicId));
        setTopic(topic);
    
        if (topic && topic.statements) {
            const yesStatementIds = [];
            const newTipSelections = {};
    
            // Find "yes" answers and checked tips
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key.startsWith(`statement_${topicId}_`)) {
                    const [, , statementId] = key.split('_');
                    const value = localStorage.getItem(key);
                    
                    if (value === "1" && topic.statements.some(s => s.ID === Number(statementId))) {
                        yesStatementIds.push(Number(statementId));
                    }
                }
    
                if (key.startsWith(`tipSelections_${topicId}_`)) {
                    const [, , statementId] = key.split('_');
                    newTipSelections[statementId] = JSON.parse(localStorage.getItem(key)) || [];
                }
            }
    
            // Order "yes" answers like the original order in "topics"
            const orderedYesStatementIds = topic.statements
                .filter(statement => yesStatementIds.includes(statement.ID))
                .map(statement => statement.ID);
            setAnsweredYesStatementIds(orderedYesStatementIds);
    
            // Save checked tips to state
            orderedYesStatementIds.forEach(statementId => {
                setTipSelections(prevSelections => ({
                    ...prevSelections,
                    [statementId]: newTipSelections[statementId] || Array(topic.statements.find(s => s.ID === Number(statementId)).tips.length).fill(false)
                }));
            });
        }


    }, [topicId, topics]);

    // Save USER TIPS to state
    useEffect(() => {
        const storedUserTips = localStorage.getItem(`userTips_${topicId}`);
        if (storedUserTips) {
            setUserTips(JSON.parse(storedUserTips));
        }
    }, [topicId]);

    // Save USER TIPS to localStorage
    useEffect(() => {
        if (userTips && Object.keys(userTips).length > 0) {
            localStorage.setItem(`userTips_${topicId}`, JSON.stringify(userTips));
        }
    }, [userTips, topicId]);

    // Tip selection changed: update state & localStorage
    const handleTipSelectionChange = (statementId, tipIndex, isChecked) => {
        const updatedSelections = {
            ...tipSelections,
            [statementId]: {
                ...tipSelections[statementId],
                [tipIndex]: isChecked
            }
        };
        setTipSelections(updatedSelections);
        localStorage.setItem(`tipSelections_${topicId}_${statementId}`, JSON.stringify(updatedSelections[statementId]));
    };

    // USER TIP add new
    const handleAddUserTip = (statementId) => {
        if (newTipText[statementId]) {
            const tip = newTipText[statementId].trim();
            setUserTips(prevTips => ({
                ...prevTips,
                [statementId]: [...(prevTips[statementId] || []), tip]
            }));
            setNewTipText(prev => ({ ...prev, [statementId]: "" })); // Clear textarea after adding tip
        } else {
            alert(texts.summary_no_tip_entered_text);
        }
    };

    // USER TIP change
    const handleNewTipTextChange = (statementId, text) => {
        setNewTipText(prev => ({ ...prev, [statementId]: text }));
    };

    // USER TIP delete
    const handleDeleteUserTip = (statementId, index) => {
        setUserTips(prevTips => ({
            ...prevTips,
            [statementId]: prevTips[statementId].filter((_, i) => i !== index)
        }));
    };
    
    // pdfDownloadLink inside useMemo, so that it updates whenever user makes changes
    const pdfDownloadLink = useMemo(() => {
        if ( topic ) {
            return (
                <>
                    <PDFDownloadLink
                        className="save-strategy"
                        document={<MyStrategy
                                    topic={topic}
                                    answeredYesStatementIds={answeredYesStatementIds}
                                    tipSelections={tipSelections}
                                    userTips={userTips}
                                    texts={texts}
                                />}
                        fileName={"strategia-" + topic.slug + ".pdf"}
                    >
                        {texts.summary_save_strategy_text}
                    </PDFDownloadLink>
                </>
            )
        }
    }, [topic, answeredYesStatementIds, tipSelections, userTips, texts]);

    if ( topic && texts.length !== 0 && topics.length !== 0 ) {
        return (
            <>
                <Breadcrumb topics={topics} texts={texts} />
    
                <div className={`eaut-main-content summary ${topic.theme_color}`}>
                    <h1>{texts.summary_title}</h1>
    
                    <p className="ingress">{texts.summary_ingress}</p>
    
                    <div className="my-strategy-text">
                        <h2>{texts.summary_my_strategy_title}</h2>
                        <p>{texts.summary_my_strategy_text}</p>
                    </div>
    
                    {answeredYesStatementIds.length === 0 ? (
                        <p>{texts.summary_all_no_answers_text}</p>
                    ) : (
                        answeredYesStatementIds.map((id, index) => {
                            const statement = topic?.statements.find(s => s.ID === id);
    
                            return (
                                <React.Fragment key={index}>
                                    {/* 1. Väittämä johon vastattu "Kyllä" */}
                                    <div className="statement-link-icon">
                                        <img alt="" src={texts.general_site_url + '/wp-content/plugins/pwd-eautismiliitto/assets/circle-check-' + topic.theme_color + '.png'} />
                                        <Link to={`/statement/${topicId}/${statement.ID}`}>{topic.title}: {texts.summary_statement_text} {statement.ID}</Link>
                                    </div>
                                    <h2 className="title-statement">"{statement.text}"</h2>
    
                                    {/* 2. Väittämään liittyvät vinkit */}
                                    {statement.tips && statement.tips.length > 0 && (
                                        statement.tips.map((tip, tipIndex) => (
                                            <div className={tipSelections[statement.ID] && tipSelections[statement.ID][tipIndex] ? 'tip tip--checked' : 'tip tip--unchecked'} key={tipIndex}>
                                                <p style={{ marginTop: 0 }}><strong>{texts.summary_tip_text} {tipIndex + 1}</strong></p>
                                                <p style={{ marginTop: 0 }}>{tip}</p>
                                                <div className="check-tip">
                                                    <input
                                                        type="checkbox"
                                                        checked={tipSelections[statement.ID] && tipSelections[statement.ID][tipIndex]}
                                                        onChange={(e) => handleTipSelectionChange(statement.ID, tipIndex, e.target.checked)}
                                                        id={'tip-' + [statement.ID][tipIndex]}
                                                    />
                                                    <label for={'tip-' + [statement.ID][tipIndex]}>
                                                        {texts.summary_tip_could_work_text} <span className="screen-reader-text">({tip})</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))
                                    )}

                                    {/* 3. Omat vinkit */}
                                    {userTips[statement.ID] && userTips[statement.ID].map((userTip, tipIndex) => (
                                        <div className="tip tip--user" key={tipIndex}>
                                            <p style={{ marginTop: 0 }}><strong>{texts.summary_user_tip_text} {tipIndex + 1}</strong></p>
                                            <p>{userTip}</p>
                                            <button onClick={() => handleDeleteUserTip(statement.ID, tipIndex)}>
                                                <img alt="" src={texts.general_site_url + '/wp-content/plugins/pwd-eautismiliitto/assets/trash.png'} />
                                                {texts.summary_delete_tip_text}
                                            </button>
                                        </div>
                                    ))}

                                    {/* 4. Lisää oma vinkki */}
                                    <div className="tip tip--new-tip">
                                        <p style={{ marginTop: 0 }}><strong>{texts.summary_write_tip_text}</strong></p>
                                        <textarea
                                            placeholder={texts.summary_write_tip_text}
                                            value={newTipText[statement.ID] || ""}
                                            rows="5"
                                            onChange={(e) => handleNewTipTextChange(statement.ID, e.target.value)}
                                        />
                                        <button className="save" onClick={() => handleAddUserTip(statement.ID)}>
                                            <img alt="" src={texts.general_site_url + '/wp-content/plugins/pwd-eautismiliitto/assets/save.png'} />
                                            {texts.summary_save_tip_text}
                                        </button>
                                    </div>
                                    

                                </React.Fragment>
                            );
                        })
                    )}
    
                    <PWDModal texts={texts} show_open_button={false} white_bg={true} />

                    <div className="buttons">
                        {answeredYesStatementIds.length !== 0 && (
                            pdfDownloadLink
                        )}
                        <Link className="choose-other-topic" to="/topics">{texts.general_choose_other_topic_text}</Link>
                    </div>
                </div>

                <Footer texts={texts} />
            </>
        );
    }

};

export default Summary;
